import React from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { APP_EVENTS, logHubxEvent } from '@/app/providers/analytics';
import { NextUI } from '@/components/NextUI';
import { formatAnalyticsSource } from '@/helpers/common';
import { buildStructuredAnalyticsSource } from '@/lib/analytics/utils/source';
import useCustomSearchParams from '@/hooks/useCustomSearchParams';
import StatusGlow from './EditShareModal.icons/StatusGlow';
import StatusCheck from './EditShareModal.icons/StatusCheck';
import DownloadArrow from './EditShareModal.icons/DownloadArrow';
import CloseCross from './EditShareModal.icons/CloseCross';
import {
  OPTIONS,
  DOWNLOAD_BUTTON_LABEL,
  FileTypeIcon,
} from './EditShareModal.constants';
import { EditShareOptionId, EditShareModalData } from './EditShareModal.types';
import EditShareDownloadReadyModal from './EditShareModal.downloadReady';

const EditShareDefaultModal = ({
  data,
  hideModal,
}: {
  data?: EditShareModalData;
  hideModal: () => void;
}) => {
  const { searchParams } = useCustomSearchParams();
  const tUnlock = useTranslations('unlock_pdf_flow');
  const isUnlockPresentation = data?.presentation === 'unlock';
  const hasUnlockFeedback =
    isUnlockPresentation && Boolean(data?.onUnlockFeedbackContainerChange);
  const toolType = data?.toolType ?? searchParams.get('tool') ?? undefined;
  const formattedToolType = React.useMemo(
    () => formatAnalyticsSource(toolType),
    [toolType]
  );
  const analyticsSource = React.useMemo(
    () => buildStructuredAnalyticsSource(toolType, data?.originEntryPoint),
    [data?.originEntryPoint, toolType]
  );
  const [selectedOptionId, setSelectedOptionId] =
    React.useState<EditShareOptionId>(data?.selectedOptionId ?? 'pdf');
  const [fileNameValue, setFileNameValue] = React.useState(
    data?.fileName ?? ''
  );

  React.useEffect(() => {
    logHubxEvent({
      eventName: APP_EVENTS.LND_DOWNLOAD_POPUP,
      source: formattedToolType,
      event_params: {
        source: analyticsSource,
        ...(typeof data?.downloadVariant === 'number'
          ? { downloadVariant: data.downloadVariant }
          : {}),
      },
    });
  }, [analyticsSource, data?.downloadVariant, formattedToolType]);

  const resolveFileName = React.useCallback(() => {
    const trimmed = fileNameValue.trim();
    return trimmed.length > 0 ? trimmed : (data?.fileName ?? '');
  }, [data?.fileName, fileNameValue]);

  const handleClose = React.useCallback(() => {
    data?.onContinueEdit?.(resolveFileName());
    hideModal();
  }, [data, hideModal, resolveFileName]);

  const handleContinueEdit = React.useCallback(() => {
    logHubxEvent({
      eventName: APP_EVENTS.BTN_CONTINUE_POPUP,
      source: formattedToolType,
      event_params: {
        source: analyticsSource,
      },
    });
    data?.onContinueEdit?.(resolveFileName());
    hideModal();
  }, [analyticsSource, data, formattedToolType, hideModal, resolveFileName]);

  const handleConfirm = React.useCallback(() => {
    const finalFileName = resolveFileName();
    hideModal();
    data?.onConfirm?.(selectedOptionId, finalFileName);
  }, [data, hideModal, resolveFileName, selectedOptionId]);

  return (
    <NextUI.Modal
      className={clsx('mx-4 max-w-[670px]', hasUnlockFeedback && '!my-0')}
      classNames={{
        content: hasUnlockFeedback
          ? 'relative flex max-h-[calc(100dvh-32px)] flex-col gap-6 overflow-visible !bg-transparent !p-0 shadow-none'
          : 'relative max-h-[calc(100dvh-32px)] overflow-x-hidden overflow-y-auto rounded-2xl p-4 shadow-none sm:max-h-[calc(100dvh-128px)] md:px-12 md:py-8',
        backdrop: isUnlockPresentation
          ? 'bg-[rgba(124,124,124,0.5)]'
          : 'bg-[rgba(26,26,26,0.18)] backdrop-blur-[1.2px]',
        closeButton:
          'right-2 top-2 z-10 text-[#787887] opacity-100 md:right-4 md:top-4',
      }}
      defaultOpen={true}
      name="EditShare"
      backdrop={isUnlockPresentation ? 'opaque' : 'blur'}
      hideCloseButton={isUnlockPresentation}
      closeButton={
        <button type="button">
          <CloseCross className="h-6 w-6" />
        </button>
      }
      onClose={handleClose}
    >
      <div
        data-unlock-download-card={hasUnlockFeedback || undefined}
        className={
          hasUnlockFeedback
            ? 'relative min-h-0 overflow-x-hidden overflow-y-auto rounded-2xl bg-white p-4 md:px-12 md:py-8 [@media(max-height:760px)]:py-4'
            : 'contents'
        }
      >
        <StatusGlow className="pointer-events-none absolute left-0 top-0 h-[152px] w-[670px]" />

        <div
          className={clsx(
            'relative flex flex-col gap-4 md:gap-8',
            hasUnlockFeedback && '[@media(max-height:760px)]:gap-4'
          )}
        >
          <div
            className={clsx(
              'flex flex-col items-center gap-2 text-center md:gap-4 md:py-4',
              hasUnlockFeedback && '[@media(max-height:760px)]:py-0'
            )}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-[0px_24px_32px_0px_rgba(0,0,0,0.08)] md:h-16 md:w-16">
              <StatusCheck className="h-6 w-6 text-[#31CA93] md:h-10 md:w-10" />
            </div>

            <div className="flex flex-col items-center gap-1">
              <h2
                className={clsx(
                  'font-barlow text-[20px] font-semibold text-[#454655] md:text-[28px]',
                  isUnlockPresentation ? 'leading-[normal]' : 'leading-normal'
                )}
              >
                {isUnlockPresentation
                  ? tUnlock('download_title')
                  : 'Your document is ready!'}
              </h2>
              <p
                className={clsx(
                  'font-barlow text-[15px] text-[#787887] md:text-[17px]',
                  isUnlockPresentation ? 'leading-[normal]' : 'leading-normal'
                )}
              >
                {isUnlockPresentation
                  ? tUnlock('download_description')
                  : 'Choose a format to download your file, or continue editing your PDF.'}
              </p>
            </div>
          </div>

          <div
            className={clsx(
              'flex flex-col',
              isUnlockPresentation ? 'gap-4' : 'gap-0'
            )}
          >
            {isUnlockPresentation ? (
              <p className="font-barlow text-[17px] font-semibold leading-[normal] text-[#454655]">
                {tUnlock('export_as_label')}
              </p>
            ) : null}
            <div className="grid grid-cols-1 gap-1 md:grid-cols-2 md:gap-3">
              {OPTIONS.map((option) => {
                const isSelected = option.id === selectedOptionId;

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setSelectedOptionId(option.id)}
                    className={clsx(
                      'flex items-center gap-2 rounded-xl px-4 text-left transition-colors',
                      isUnlockPresentation ? 'py-[11px]' : 'py-3',
                      isSelected
                        ? 'border border-[#6FA9FF] bg-[#F2F8FD]'
                        : 'border border-transparent bg-[rgba(0,0,0,0.04)]'
                    )}
                  >
                    <span className="flex flex-1 items-center gap-2">
                      <span
                        className={clsx(
                          'flex h-4 w-4 items-center justify-center rounded-full border',
                          isSelected ? 'border-[#4B7EFF]' : 'border-[#CECFD4]'
                        )}
                      >
                        {isSelected ? (
                          <span className="h-2 w-2 rounded-full bg-[#4B7EFF]" />
                        ) : null}
                      </span>
                      <span
                        className={clsx(
                          'font-barlow text-[16px] font-medium text-[#454655] md:text-[20px]',
                          isUnlockPresentation && 'leading-[normal]'
                        )}
                      >
                        {option.label}
                      </span>
                    </span>

                    <FileTypeIcon
                      color={option.color}
                      glyphTop={option.glyphTop}
                      Glyph={option.Glyph}
                      badge={option.badge}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <p
              className={clsx(
                'font-barlow text-[17px] text-[#454655]',
                isUnlockPresentation
                  ? 'font-semibold leading-[normal]'
                  : 'font-medium'
              )}
            >
              {isUnlockPresentation ? tUnlock('file_name_label') : 'File name'}
            </p>
            <NextUI.Input
              value={fileNameValue}
              onChange={(e) => setFileNameValue(e.target.value)}
              variant="outline"
              radius="sm"
              classNames={{
                inputWrapper: 'h-12 border-[#CECFD4]',
                input: 'font-barlow text-[15px] text-[#454655]',
              }}
            />
          </div>

          <div className="flex flex-col gap-2 md:flex-row">
            <NextUI.Button
              variant="outline"
              className="w-full border-[#787887] md:flex-1"
              onClick={handleContinueEdit}
            >
              {isUnlockPresentation
                ? tUnlock('cancel_button')
                : 'Continue editing'}
            </NextUI.Button>

            <NextUI.Button
              className="w-full gap-1 md:flex-1"
              onClick={handleConfirm}
            >
              <DownloadArrow className="h-6 w-6" />
              {DOWNLOAD_BUTTON_LABEL[selectedOptionId]}
            </NextUI.Button>
          </div>
        </div>
      </div>
      {hasUnlockFeedback ? (
        <div
          ref={data?.onUnlockFeedbackContainerChange}
          className="flex min-h-14 w-full shrink-0 justify-center [@media(min-width:1800px)_and_(min-height:1000px)]:hidden"
        />
      ) : null}
    </NextUI.Modal>
  );
};

const EditShareModal = (props: {
  data?: EditShareModalData;
  hideModal: () => void;
}) =>
  props.data?.downloadVariant === 1 ? (
    <EditShareDownloadReadyModal {...props} />
  ) : (
    <EditShareDefaultModal {...props} />
  );

export default EditShareModal;
